import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSession } from "next-auth/react";
import { Heading, Text } from "@otrium/atoms";
import { Box } from "@otrium/core";
import { FormikProps } from "formik";
import { t, Trans } from "@lingui/macro";
import { useUpdateCartCountry } from "src/hooks/useUpdateCartCountry";
import { Checkbox } from "src/atoms/Checkbox";
import { clearAddressFields } from "src/molecules/AddressForm/utils";
import { FieldWithError } from "src/molecules/FieldWithError";
import { CheckoutAddressForm } from "./CheckoutAddressForm";
import { CreateAccountBlock } from "src/molecules/CreateAccountBlock";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useLingui } from "@lingui/react";
import { CartInfo } from "src/types/cartInfo";
import { PasswordInput } from "src/atoms/PasswordInput";
import {
  CheckoutFormWrapper,
  FieldsGroup,
  FormSection,
} from "./CheckoutForm.styled";
import type { Checkout } from "src/types/forms";
import { isSignUpAtCheckoutSchema } from "src/utils/type-guards";
import { isEqual } from "lodash-es";

type CheckoutFormProps = {
  formik: FormikProps<Checkout>;
  isUpdateUserProfile: boolean;
  cartInfo: CartInfo;
  setIsUpdateUserProfile: (value: boolean) => void;
  onDifferentBillingAddressToggle: (value: boolean) => void;
  setLoadingUpdateCartCountry: (value: boolean) => void;
};

function CheckoutForm({
  formik,
  isUpdateUserProfile,
  cartInfo,
  setIsUpdateUserProfile,
  onDifferentBillingAddressToggle,
  setLoadingUpdateCartCountry,
}: CheckoutFormProps): JSX.Element {
  const { data: session } = useSession();
  const { _ } = useLingui();
  const [isDifferentBillingAddress, setIsDifferentBillingAddress] =
    useState<boolean>(false);

  const { i18n } = useLingui();

  const currentShippingCountryRef = useRef(
    formik.values.shippingAddress.country
  );
  const currentBillingCountryRef = useRef(formik.values.billingAddress.country);

  const isLoggedIn = useIsLoggedIn();

  const { updateCartCountry, loading: loadingUpdateCartCountry } =
    useUpdateCartCountry();

  const shouldCreateAccount = useMemo(() => {
    return isSignUpAtCheckoutSchema(formik.values)
      ? formik.values.shouldCreateAccount
      : false;
  }, [formik.values]);

  const onShouldCreateAccountChange = useCallback(() => {
    formik
      .setFieldValue("shouldCreateAccount", !shouldCreateAccount)
      .then(() => {
        formik
          .setFieldValue("password", "")
          .then(() => {
            void formik.setFieldTouched("password", false);
          })
          .catch(() => {
            console.error("Error setting password field value");
          });
      })
      .catch(() => {
        console.error("Error setting shouldCreateAccount field value");
      });
  }, [formik, shouldCreateAccount]);

  useEffect(() => {
    setLoadingUpdateCartCountry(loadingUpdateCartCountry);
  }, [loadingUpdateCartCountry, setLoadingUpdateCartCountry]);

  useEffect(() => {
    onDifferentBillingAddressToggle(isDifferentBillingAddress);
  }, [onDifferentBillingAddressToggle, isDifferentBillingAddress]);

  useEffect(() => {
    if (isDifferentBillingAddress) return;

    if (!isEqual(formik.values.billingAddress, formik.values.shippingAddress)) {
      void formik.setFieldValue(
        "billingAddress",
        formik.values.shippingAddress
      );
    }
  }, [isDifferentBillingAddress, formik, formik.values.shippingAddress]);

  useEffect(() => {
    if (
      currentShippingCountryRef.current ===
      formik.values.shippingAddress.country
    ) {
      return;
    }

    updateCartCountry(formik.values, cartInfo);
    clearAddressFields(formik, "shippingAddress");

    currentShippingCountryRef.current = formik.values.shippingAddress.country;
  }, [formik, cartInfo, updateCartCountry]);

  useEffect(() => {
    if (
      currentBillingCountryRef.current === formik.values.billingAddress.country
    ) {
      return;
    }

    if (isDifferentBillingAddress) {
      clearAddressFields(formik, "billingAddress");
      currentBillingCountryRef.current = formik.values.billingAddress.country;
    }
  }, [formik, isDifferentBillingAddress]);

  const handleChangeIsDifferentBillingAddress = useCallback(() => {
    setIsDifferentBillingAddress((prev) => {
      if (!!prev) {
        void formik.setFieldValue(
          "billingAddress",
          formik.values.shippingAddress
        );
        void formik.setTouched({
          ...formik.touched,
          billingAddress: formik.touched.shippingAddress,
        });
        if (formik.errors.shippingAddress) {
          formik.setErrors({
            ...formik.errors,
            billingAddress: formik.errors.shippingAddress,
          });
        }
      }

      return !prev;
    });
  }, [formik]);

  return (
    <CheckoutFormWrapper>
      <FormSection>
        <Heading as="h3" variant="h3">
          <Trans>Contact information</Trans>
        </Heading>
        <FieldsGroup>
          <FieldWithError
            type="input"
            formType="checkout"
            data-testid="checkout-info-form-first-name"
            formik={formik}
            label={`${t(i18n)`First name`} *`}
            field="firstName"
          />
          <FieldWithError
            type="input"
            formType="checkout"
            data-testid="checkout-info-form-last-name"
            formik={formik}
            label={`${t(i18n)`Last name`} *`}
            field="lastName"
          />
          <FieldWithError
            type="input"
            formType="checkout"
            data-testid="checkout-info-form-email"
            formik={formik}
            disabled={!!session?.user?.email}
            label={`${t(i18n)`Email`} *`}
            field="email"
          />
          <FieldWithError
            type="phone"
            formType="checkout"
            data-testid="checkout-info-form-phone"
            formik={formik}
            label={`${t(i18n)`Phone`}`}
            field="phone"
          />
        </FieldsGroup>
      </FormSection>

      {!isLoggedIn ? (
        <CreateAccountBlock as="h3" variant="h3">
          <Checkbox
            checked={shouldCreateAccount}
            onChange={onShouldCreateAccountChange}
          >
            <Trans>sign_up_checkout.checkbox_label</Trans>
          </Checkbox>
          <PasswordInput
            data-testid="login-form-password-input"
            placeholder={t(i18n)`sign_up_checkout.password_placeholder`}
            disabled={!shouldCreateAccount}
            {...formik.getFieldProps("password")}
          />
          {formik.errors.password && formik.touched.password ? (
            <Text
              as="p"
              mt={-2}
              color="semantic.red"
              textAlign="left"
              fontSize={0}
              lineHeight="1.43"
            >
              <span role="alert" id="password-error-alert">
                {_(formik.errors.password)}
              </span>
            </Text>
          ) : null}
        </CreateAccountBlock>
      ) : null}

      <Heading as="h3" variant="h3">
        {!isDifferentBillingAddress ? (
          <Trans>Shipping & billing address</Trans>
        ) : (
          <Trans>Shipping address</Trans>
        )}
      </Heading>

      <CheckoutAddressForm formik={formik} addressType="shippingAddress" />

      {isLoggedIn ? (
        <Checkbox
          checked={isUpdateUserProfile}
          onChange={() => setIsUpdateUserProfile(!isUpdateUserProfile)}
        >
          <Box sx={{ whiteSpace: "initial" }}>
            <Trans>checkout_form.update_shipping_address_in_profile</Trans>
          </Box>
        </Checkbox>
      ) : null}

      <section>
        <Checkbox
          checked={isDifferentBillingAddress}
          onChange={handleChangeIsDifferentBillingAddress}
        >
          <Box sx={{ whiteSpace: "initial" }}>
            <Trans>Billing address different to shipping address</Trans>
          </Box>
        </Checkbox>
      </section>

      {isDifferentBillingAddress && (
        <>
          <Heading as="h3" variant="h3">
            <Trans>Billing address</Trans>
          </Heading>

          <CheckoutAddressForm formik={formik} addressType="billingAddress" />
        </>
      )}
    </CheckoutFormWrapper>
  );
}

export { CheckoutForm };
